/**
 * Currency amount inside math delimiters, e.g. `$12,500$` (agent sometimes wraps
 * dollar amounts as math). Digits with optional thousands separators / decimals.
 */
const CURRENCY_AMOUNT_PATTERN = /^\d{1,3}(?:,\d{3})*(?:\.\d+)?$/

/** Max length for space-free tokens promoted to math (`$R(t)$`, `$5:1$`). */
const SHORT_MATH_TOKEN_MAX_LENGTH = 40

/** Max length for spaced inline equations that still look like math. */
const INLINE_EQUATION_MAX_LENGTH = 120

/**
 * Cap agent markdown before regex-free parsing to bound worst-case work (Sonar S5852).
 * Precedent: creativeAssets ReDoS guards.
 */
export const MAX_MARKDOWN_PREPROCESS_LENGTH = 500_000

const DISPLAY_MATH_FENCE = '$$'
const DOLLAR = '$'
const NEWLINE = '\n'
const PIPE = '|'
const MIN_ALIGNMENT_DASHES = 3

const truncateForPreprocess = (text: string): string =>
  text.length > MAX_MARKDOWN_PREPROCESS_LENGTH ? text.slice(0, MAX_MARKDOWN_PREPROCESS_LENGTH) : text

const trimNewlines = (value: string): string => {
  let start = 0
  let end = value.length
  while (start < end && value[start] === NEWLINE) {
    start += 1
  }
  while (end > start && value[end - 1] === NEWLINE) {
    end -= 1
  }
  return value.slice(start, end)
}

/**
 * True when `$...$` content should render as math — not prose trapped between
 * two currency dollar signs (e.g. `$100 rising to $`).
 */
export const looksLikeInlineMath = (content: string): boolean => {
  const trimmed = content.trim()
  if (trimmed.length === 0 || CURRENCY_AMOUNT_PATTERN.test(trimmed)) {
    return false
  }

  if (trimmed.includes('\\') || /[_^]/.test(trimmed)) {
    return true
  }

  if (!/\s/.test(trimmed) && trimmed.length <= SHORT_MATH_TOKEN_MAX_LENGTH) {
    return true
  }

  return /[=+*/<>]/.test(trimmed) && trimmed.length <= INLINE_EQUATION_MAX_LENGTH
}

/**
 * Rewrite single-dollar math (`$O_i$`, `$\rightarrow$`) to double-dollar form
 * (`$$O_i$$`) so it renders when `singleDollarTextMath` is disabled.
 *
 * Currency like `$100` / `$1,000` (no closing `$`) is left alone. When the
 * model wraps a currency amount as `$12,500$`, the closing `$` is stripped so
 * it still reads as currency rather than KaTeX.
 *
 * Index-based scan avoids ReDoS from lookbehind + lazy quantifier regex (Sonar S5852).
 */
export const normalizeSingleDollarLatexCommands = (text: string): string => {
  const source = truncateForPreprocess(text)
  let result = ''
  let index = 0

  while (index < source.length) {
    if (source.startsWith(DISPLAY_MATH_FENCE, index)) {
      result += DISPLAY_MATH_FENCE
      index += DISPLAY_MATH_FENCE.length
      continue
    }

    if (source[index] === DOLLAR) {
      let cursor = index + 1
      let closeIndex = -1

      while (cursor < source.length && source[cursor] !== NEWLINE) {
        if (source.startsWith(DISPLAY_MATH_FENCE, cursor)) {
          break
        }
        if (source[cursor] === DOLLAR) {
          closeIndex = cursor
          break
        }
        cursor += 1
      }

      if (closeIndex !== -1) {
        const content = source.slice(index + 1, closeIndex)
        const trimmed = content.trim()
        if (CURRENCY_AMOUNT_PATTERN.test(trimmed)) {
          result += `${DOLLAR}${trimmed}`
        } else if (!looksLikeInlineMath(content)) {
          result += source.slice(index, closeIndex + 1)
        } else {
          result += `${DISPLAY_MATH_FENCE}${content}${DISPLAY_MATH_FENCE}`
        }
        index = closeIndex + 1
        continue
      }
    }

    result += source[index]
    index += 1
  }

  return result
}

/**
 * Put opening/closing `$$` on their own lines for multiline display math.
 *
 * remark-math mishandles agent-style blocks like:
 * `$$A = \begin{pmatrix}\n...\n\end{pmatrix}$$`
 * (content on the opening fence line) — KaTeX then sees a broken fragment and
 * renders red parse errors on `&` / matrix rows. Single-line `$$...$$` is left
 * alone (inline math).
 *
 * Index-based fence pairing avoids ReDoS from `[\s\S]+?` (Sonar S5852).
 */
export const normalizeDisplayMathFences = (text: string): string => {
  const source = truncateForPreprocess(text)
  let result = ''
  let index = 0

  while (index < source.length) {
    const open = source.indexOf(DISPLAY_MATH_FENCE, index)
    if (open === -1) {
      result += source.slice(index)
      break
    }

    result += source.slice(index, open)
    const contentStart = open + DISPLAY_MATH_FENCE.length
    const close = source.indexOf(DISPLAY_MATH_FENCE, contentStart)
    if (close === -1) {
      result += source.slice(open)
      break
    }

    const content = source.slice(contentStart, close)
    if (!content.includes(NEWLINE)) {
      result += source.slice(open, close + DISPLAY_MATH_FENCE.length)
    } else {
      const trimmed = trimNewlines(content)
      result += `${DISPLAY_MATH_FENCE}${NEWLINE}${trimmed}${NEWLINE}${DISPLAY_MATH_FENCE}`
    }
    index = close + DISPLAY_MATH_FENCE.length
  }

  return result
}

const isAlignmentCell = (cell: string): boolean => {
  const trimmed = cell.trim()
  let index = 0
  if (trimmed[index] === ':') {
    index += 1
  }
  let dashCount = 0
  while (index < trimmed.length && trimmed[index] === '-') {
    dashCount += 1
    index += 1
  }
  if (dashCount < MIN_ALIGNMENT_DASHES) {
    return false
  }
  if (trimmed[index] === ':') {
    index += 1
  }
  return index === trimmed.length && trimmed.length > 0
}

/** GFM separator row without a leading `|` (e.g. `--- | ---`). */
const isGfmAlignmentRow = (trimmed: string): boolean => {
  if (!trimmed.includes(PIPE)) {
    return false
  }

  const rawCells = trimmed.split(PIPE)
  const cells: string[] = []
  for (let cellIndex = 0; cellIndex < rawCells.length; cellIndex++) {
    const cell = rawCells[cellIndex]
    const isEdge = cellIndex === 0 || cellIndex === rawCells.length - 1
    if (isEdge && cell.trim() === '') {
      continue
    }
    cells.push(cell)
  }

  return cells.length >= 1 && cells.every(isAlignmentCell)
}

/** Returns true when a markdown line is part of a GFM table block. */
export const isTableLine = (line: string): boolean => {
  const trimmed = truncateForPreprocess(line).trim()
  if (trimmed.length === 0) {
    return false
  }

  if (trimmed.startsWith(PIPE)) {
    return true
  }

  return isGfmAlignmentRow(trimmed)
}

const isDisplayMathBlock = (part: string): boolean =>
  part.startsWith(DISPLAY_MATH_FENCE) && part.endsWith(DISPLAY_MATH_FENCE)

/**
 * Split text into alternating prose / `$$…$$` parts without regex (Sonar S5852).
 */
const splitByDisplayMathBlocks = (text: string): string[] => {
  const parts: string[] = []
  let index = 0

  while (index < text.length) {
    const open = text.indexOf(DISPLAY_MATH_FENCE, index)
    if (open === -1) {
      parts.push(text.slice(index))
      break
    }

    if (open > index) {
      parts.push(text.slice(index, open))
    }

    const contentStart = open + DISPLAY_MATH_FENCE.length
    const close = text.indexOf(DISPLAY_MATH_FENCE, contentStart)
    if (close === -1) {
      parts.push(text.slice(open))
      break
    }

    parts.push(text.slice(open, close + DISPLAY_MATH_FENCE.length))
    index = close + DISPLAY_MATH_FENCE.length
  }

  return parts
}

/**
 * Turn single newlines into hard breaks (trailing double-space) so they render as <br>.
 * Skips table rows so GFM table syntax stays intact for remark-gfm.
 * Skips `$$...$$` display-math blocks so fence lines stay parseable.
 */
export const withHardBreaks = (text: string): string => {
  const source = truncateForPreprocess(text)
  const parts = splitByDisplayMathBlocks(source)

  return parts
    .map((part) => {
      if (isDisplayMathBlock(part)) {
        return part
      }

      const lines = part.split(NEWLINE)
      const output: string[] = []

      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        let line = lines[lineIndex]
        const nextLine = lines[lineIndex + 1]

        if (nextLine !== undefined) {
          const isSingleNewline = nextLine !== ''
          const tableContext = isTableLine(line) || isTableLine(nextLine)

          if (isSingleNewline && line.length > 0 && !tableContext && !line.endsWith('  ')) {
            line = `${line}  `
          }
        }

        output.push(line)
      }

      return output.join(NEWLINE)
    })
    .join('')
}

const COPILOT_INLINE_TOKEN_OPEN = '$[['
/** Structural close; trailing `$` is optional (models often drop it). */
const COPILOT_INLINE_TOKEN_CLOSE = ']]'
const COPILOT_INLINE_ID_ATTR_RE = /\bid=(?:"([^"]+)"|'([^']+)')/
const COPILOT_INLINE_TYPE_ATTR_RE = /\btype=(?:"([^"]+)"|'([^']+)')/

/** Inline token types the Alvie markdown renderer knows how to display. */
export const KNOWN_COPILOT_INLINE_TOKEN_TYPES = new Set(['chart'])

const parseCopilotInlineTokenAttributes = (inner: string): { id: string | undefined; type: string | undefined } => {
  const idMatch = COPILOT_INLINE_ID_ATTR_RE.exec(inner)
  const typeMatch = COPILOT_INLINE_TYPE_ATTR_RE.exec(inner)

  return {
    id: idMatch?.[1] ?? idMatch?.[2],
    type: typeMatch?.[1] ?? typeMatch?.[2]
  }
}

const stripIncompleteCopilotInlineTokenSuffix = (text: string): string => {
  const openIndex = text.lastIndexOf(COPILOT_INLINE_TOKEN_OPEN)
  if (openIndex === -1) {
    return text
  }

  const closeIndex = text.indexOf(COPILOT_INLINE_TOKEN_CLOSE, openIndex + COPILOT_INLINE_TOKEN_OPEN.length)
  if (closeIndex === -1) {
    return text.slice(0, openIndex)
  }

  return text
}

const toChartTokenElement = (id: string, type: string): string =>
  `<chart-token data-id="${id}" data-type="${type}"></chart-token>`

const renderKnownCopilotInlineToken = (inner: string): string => {
  const { id, type } = parseCopilotInlineTokenAttributes(inner)
  if (id == null || type == null || !KNOWN_COPILOT_INLINE_TOKEN_TYPES.has(type)) {
    return ''
  }

  return toChartTokenElement(id, type)
}

/**
 * Index after a structural `]]` close, consuming an optional trailing `$` so a
 * correct `]]$` does not leave a stray `$` in the transcript.
 */
const indexAfterCopilotInlineTokenClose = (source: string, closeIndex: number): number => {
  let index = closeIndex + COPILOT_INLINE_TOKEN_CLOSE.length
  if (source[index] === '$') {
    index += 1
  }
  return index
}

/**
 * Replaces known `$[[ … ]]$` Copilot inline tokens with raw HTML custom elements for rehype.
 * The trailing `$` is optional so model output that omits it still renders.
 * Unknown or malformed tokens are removed so Flow can stream new token types without leaking
 * raw syntax into the transcript.
 */
export const replaceCopilotInlineTokens = (markdown: string): string => {
  const source = stripIncompleteCopilotInlineTokenSuffix(truncateForPreprocess(markdown))
  let result = ''
  let index = 0

  while (index < source.length) {
    const open = source.indexOf(COPILOT_INLINE_TOKEN_OPEN, index)
    if (open === -1) {
      result += source.slice(index)
      break
    }

    result += source.slice(index, open)
    const innerStart = open + COPILOT_INLINE_TOKEN_OPEN.length
    const close = source.indexOf(COPILOT_INLINE_TOKEN_CLOSE, innerStart)
    if (close === -1) {
      result += source.slice(open)
      break
    }

    result += renderKnownCopilotInlineToken(source.slice(innerStart, close))
    index = indexAfterCopilotInlineTokenClose(source, close)
  }

  return result
}
